import { useEffect, useState } from "react";

type User = { id: string; email: string; name?: string; role: string };

export default function UsersTable({ api }: { api: string }) {
  const [users, setUsers] = useState<User[]>([]);
  const [modalUser, setModalUser] = useState<User | null>(null);

  useEffect(() => {
    (async () => {
      const r = await fetch(`${api}/admin/users`, { credentials: "include" });
      const j = await r.json().catch(() => []);
      setUsers(Array.isArray(j) ? j : []);
    })();
  }, [api]);

  async function doReset(u: User, newPw: string) {
    const res = await fetch(`${api}/admin/users/${u.id}/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ new_password: newPw }),
    });
    if (!res.ok) {
      const j = await res.json().catch(() => ({ error: "Failed" }));
      throw new Error(j.error || "Failed");
    }
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Role</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className="border-b hover:bg-gray-50">
                <td className="py-3 px-4">{u.email}</td>
                <td className="py-3 px-4">{u.name || "—"}</td>
                <td className="py-3 px-4">{u.role}</td>
                <td className="py-3 px-4 text-right">
                  <button
                    onClick={() => setModalUser(u)}
                    className="text-xs px-3 py-1 rounded border border-blue-300 text-blue-600 hover:bg-blue-50 transition-colors"
                  >
                    Reset Password
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modalUser && (
        <ResetPasswordModal
          user={modalUser}
          onClose={() => setModalUser(null)}
          onReset={(pw) => doReset(modalUser, pw)}
        />
      )}
    </>
  );
}

function ResetPasswordModal({
  user,
  onClose,
  onReset,
}: {
  user: User;
  onClose: () => void;
  onReset: (newPw: string) => Promise<void>;
}) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const canSave = pw.length >= 8 && pw === confirm;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    setBusy(true); setErr(null);
    try {
      await onReset(pw);
      onClose();
    } catch (e: any) {
      setErr(e?.message || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Reset Password</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">✕</button>
        </div>
        <p className="text-sm text-gray-600 mb-4">User: <strong>{user.email}</strong></p>
        {err && <div className="mb-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="pw">New Password</label>
            <input id="pw" type="password" value={pw} onChange={(e)=>setPw(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1" htmlFor="confirm">Confirm Password</label>
            <input id="confirm" type="password" value={confirm} onChange={(e)=>setConfirm(e.target.value)} className="w-full px-3 py-2 border rounded" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="px-4 py-2 rounded border" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="submit"
              className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-60"
              disabled={!canSave || busy}
            >
              {busy ? "Saving…" : "Reset Password"}
            </button>
          </div>
          <p className="text-xs text-gray-500">Min 8 characters. Consider updating <code>password_changed_at</code> server-side to force re-auth.</p>
        </form>
      </div>
    </div>
  );
}